import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { getCities, createCity, updateCity, deleteCity } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminCities() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    image_url: '',
    seo_title: '',
    seo_description: '',
    is_active: true
  });

  const fetchCities = async () => {
    try {
      const response = await getCities();
      setCities(response.data);
    } catch (error) {
      toast.error('Şehirler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenDialog = (city = null) => {
    if (city) {
      setEditingCity(city);
      setFormData({
        name: city.name,
        slug: city.slug || '',
        image_url: city.image_url || '',
        seo_title: city.seo_title || '',
        seo_description: city.seo_description || '',
        is_active: city.is_active
      });
    } else {
      setEditingCity(null);
      setFormData({
        name: '',
        slug: '',
        image_url: '',
        seo_title: '',
        seo_description: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCity) {
        await updateCity(editingCity.id, formData);
        toast.success('Şehir güncellendi');
      } else {
        await createCity(formData);
        toast.success('Şehir eklendi');
      }
      setDialogOpen(false);
      fetchCities();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (city) => {
    if (!window.confirm(`${city.name} şehrini silmek istediğinize emin misiniz?`)) return;
    try {
      await deleteCity(city.id);
      toast.success('Şehir silindi');
      fetchCities();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  return (
    <div className="space-y-6 page-enter" data-testid="admin-cities">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Şehir ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-card border-white/10"
            data-testid="city-search"
          />
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-city-btn">
          <Plus className="w-4 h-4 mr-2" /> Şehir Ekle
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="w-12">Görsel</TableHead>
              <TableHead>Şehir Adı</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead className="w-24">Durum</TableHead>
              <TableHead className="w-32 text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredCities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {searchQuery ? 'Şehir bulunamadı' : 'Henüz şehir eklenmemiş'}
                </TableCell>
              </TableRow>
            ) : (
              filteredCities.map(city => (
                <TableRow key={city.id} className="border-white/10" data-testid={`city-row-${city.id}`}>
                  <TableCell>
                    {city.image_url ? (
                      <img 
                        src={city.image_url} 
                        alt={city.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{city.name}</TableCell>
                  <TableCell className="text-muted-foreground">{city.slug}</TableCell>
                  <TableCell>
                    <Badge variant={city.is_active ? 'default' : 'secondary'}>
                      {city.is_active ? 'Aktif' : 'Pasif'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleOpenDialog(city)}
                      data-testid={`edit-city-${city.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(city)}
                      data-testid={`delete-city-${city.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle>
              {editingCity ? 'Şehir Düzenle' : 'Yeni Şehir'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Şehir Adı *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Eskişehir"
                required
                className="bg-secondary border-white/10"
                data-testid="city-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (Otomatik oluşturulur)</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="eskisehir"
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="image_url">Görsel URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seo_title">SEO Başlık</Label>
              <Input
                id="seo_title"
                value={formData.seo_title}
                onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                placeholder="Eskişehir Firma Rehberi | FirmaBurada"
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seo_description">SEO Açıklama</Label>
              <Input
                id="seo_description"
                value={formData.seo_description}
                onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                placeholder="Eskişehir'deki tüm firmalar..."
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Aktif</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" data-testid="save-city-btn">
                {editingCity ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
